/**
 * The two key grammars of ADR-119 decision 2 and the wire pattern that admits both. A key names what words are
 * for, never the words, and holds no space, so it can always be told from a sentence. A catalogue key is
 * hand-written, the dotted path of a value in a catalogue. A derived key is made from a record's stable id under one
 * of the game's derived namespaces, so a record renamed in its English keeps its key. The game hands its namespace
 * list in through `keyGrammar`; the mechanism names none. A catalogue file may not take a derived namespace's name,
 * so the two never collide.
 */

/**
 * A derived namespace's name: lower-case ASCII letters and digits, starting with a letter.
 */
export const KEY_NAMESPACE_PATTERN = /^[a-z][a-z0-9]*$/;

/**
 * The longest catalogue key, in characters.
 */
export const MAX_CATALOGUE_KEY_CHARS = 64;

/**
 * The shortest key the wire admits, in characters: one letter, a dot and one more.
 */
export const MIN_WIRE_KEY_CHARS = 3;

/**
 * The longest key the wire admits, and so the longest derived key outside a local namespace, in characters.
 */
export const MAX_WIRE_KEY_CHARS = 96;

/**
 * The longest derived key under a local namespace, whose keys never travel, in characters (ADR-119 decision 2's
 * proposal, which P1.9-T166 measures).
 */
export const MAX_LOCAL_KEY_CHARS = 160;

/**
 * A catalogue key: `camelCase` ASCII segments, at least two, the first starting with a lower-case letter.
 */
export const CATALOGUE_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-z0-9][a-zA-Z0-9]*)+$/;

/**
 * A key on the wire, of either grammar.
 */
export const WIRE_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9_-]+)+$/;

/**
 * A key's first segment: the catalogue file it lives in, or a derived key's namespace.
 *
 * @param key The key.
 * @returns The text before the first dot, or the whole key when it holds no dot.
 */
export function keyNamespace(key: string): string {
  const dot = key.indexOf(".");
  return dot < 0 ? key : key.slice(0, dot);
}

/**
 * The namespace lists a game hands to `keyGrammar`.
 */
export interface KeyGrammarSpec {
  /** The derived keys' namespaces: the game's records whose words are keyed by their ids. */
  readonly derived: readonly string[];
  /** The derived namespaces whose keys never travel, since the page looks them up by an id it already holds. */
  readonly local: readonly string[];
}

/**
 * A game's key grammar, built by `keyGrammar` from its namespace lists.
 */
export interface KeyGrammar {
  /** A derived key: one of the grammar's namespaces, one or two id segments of `[a-z0-9_-]`, and a `camelCase` field. */
  readonly derivedPattern: RegExp;
  /**
   * Whether a value is a catalogue key: the pattern, at most `MAX_CATALOGUE_KEY_CHARS`, and a first segment that is
   * not one of the grammar's derived namespaces.
   */
  readonly isCatalogueKey: (value: unknown) => value is string;
  /**
   * Whether a value is a derived key: the grammar's pattern, and at most `MAX_LOCAL_KEY_CHARS` under a local
   * namespace and `MAX_WIRE_KEY_CHARS` under any other.
   */
  readonly isDerivedKey: (value: unknown) => value is string;
}

/**
 * Builds a game's key grammar from its namespace lists. The lists are copied, so the grammar never changes after it
 * is built.
 *
 * @param spec The derived namespaces and, among them, the local ones.
 * @returns The derived key pattern and the two checks that read the lists.
 * @throws RangeError When the derived list is empty, a name does not match `KEY_NAMESPACE_PATTERN`, a name is given
 *   twice, or a local name is not in the derived list.
 * @example
 * const grammar = keyGrammar({ derived: ["things", "places"], local: ["places"] });
 * grammar.isDerivedKey("things.potion.name");
 */
export function keyGrammar(spec: KeyGrammarSpec): KeyGrammar {
  if (spec.derived.length === 0) {
    throw new RangeError("a key grammar needs at least one derived namespace");
  }
  const derived = new Set<string>();
  for (const name of spec.derived) {
    if (!KEY_NAMESPACE_PATTERN.test(name)) {
      throw new RangeError(`"${name}" is not a namespace name`);
    }
    if (derived.has(name)) {
      throw new RangeError(`the derived namespace "${name}" is given twice`);
    }
    derived.add(name);
  }
  const local = new Set<string>();
  for (const name of spec.local) {
    if (!derived.has(name)) {
      throw new RangeError(`the local namespace "${name}" is not a derived namespace`);
    }
    local.add(name);
  }
  const derivedPattern = new RegExp(`^(${[...derived].join("|")})(\\.[a-z0-9_-]+){1,2}\\.[a-z][a-zA-Z0-9]*$`);
  /**
   * Whether a value is a catalogue key under this grammar.
   *
   * @param value Anything.
   * @returns True for a catalogue key whose file is not one of the derived namespaces.
   */
  function isCatalogueKey(value: unknown): value is string {
    return typeof value === "string" && value.length <= MAX_CATALOGUE_KEY_CHARS && CATALOGUE_KEY_PATTERN.test(value) && !derived.has(keyNamespace(value));
  }
  /**
   * Whether a value is a derived key under this grammar.
   *
   * @param value Anything.
   * @returns True for a derived key within its namespace's cap.
   */
  function isDerivedKey(value: unknown): value is string {
    if (typeof value !== "string" || value.length > MAX_LOCAL_KEY_CHARS || !derivedPattern.test(value)) {
      return false;
    }
    return local.has(keyNamespace(value)) || value.length <= MAX_WIRE_KEY_CHARS;
  }
  return Object.freeze({ derivedPattern, isCatalogueKey, isDerivedKey });
}

/**
 * Whether a value is a key the wire admits: the wire pattern, from `MIN_WIRE_KEY_CHARS` to `MAX_WIRE_KEY_CHARS`.
 *
 * @param value Anything.
 * @returns True for a key that may travel.
 */
export function isWireKey(value: unknown): value is string {
  return typeof value === "string" && value.length >= MIN_WIRE_KEY_CHARS && value.length <= MAX_WIRE_KEY_CHARS && WIRE_KEY_PATTERN.test(value);
}
